import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';

import { displayDateToIso, isoDateToDisplay } from '../util/organizerDateTime';
import type { DateTimeFieldProps } from './DateTimeField';

/**
 * Поле даты или времени — веб-реализация.
 *
 * <p>Внутри настоящий `<input type="date">` / `<input type="time">`, а не текстовое поле.
 * Причина — прямая жалоба владельца 2026-08-29: на телефоне через браузер по нажатию на поле
 * не появлялся календарь. Через `react-native-web` `TextInput` превращается в
 * `<input type="text">`, и мобильная Safari честно показывает ему обычную клавиатуру —
 * календаря там взяться неоткуда.
 *
 * <p>DOM-элемент напрямую, а не `TextInput` с пробросом типа: `react-native-web` не отдаёт
 * произвольные атрибуты в разметку, а `type` — единственное, что здесь и требуется. Это же
 * причина, по которой файл разделён по платформам: в нативной сборке `<input>` не существует.
 *
 * <p><b>Значение рисуем сами, поверх невидимого поля.</b> Две жалобы владельца подряд об одном
 * и том же месте:
 *
 * <ul>
 *   <li>«вёрстка даты рождения ушла» — iOS даёт `input[type=date]` собственную ширину под самую
 *       длинную дату локали и центрирует значение внутри псевдоэлемента
 *       `::-webkit-date-and-time-value`. Поле переставало слушаться контейнера, и хвост уезжал
 *       за край карточки;
 *   <li>«не переведены месяца» — браузер печатает значение в локали <b>системы</b>, а не
 *       приложения. На русском телефоне с английским интерфейсом под подписью «Date of birth»
 *       стояло «17 июня 1987 г.». Ни атрибут `lang`, ни CSS этим не управляют: формат
 *       принадлежит браузеру целиком.
 * </ul>
 *
 * <p>Обе беды — про <b>показ</b> значения, и обе снимаются одним решением: `<input>` остаётся
 * ради нативного календаря, но становится прозрачным и растянутым на всё поле (`opacity: 0`
 * прячет только изображение, попадание пальцем и системный пикер остаются), а поверх лежит наш
 * собственный текст. Формат — тот самый «ДД.ММ.ГГГГ», который приложение обещает в подсказке
 * под полем: он числовой, названий месяцев в нём нет вовсе, поэтому переводить нечего и
 * выглядит он одинаково во всех четырёх языках.
 *
 * <p>Формат наружу тот же, что у нативной версии, — «ДД.ММ.ГГГГ» и «ЧЧ:ММ». Браузер работает
 * в ISO (`гггг-мм-дд`), и перевод между ними живёт здесь: разбор в `organizerDateTime` один
 * на обе платформы, и подстраивать его под браузер значило бы завести два формата в одной
 * форме.
 */
export function DateTimeField({
  kind,
  value,
  onChange,
  placeholder,
  accessibilityLabel,
  style,
  minDate,
  maxDate,
}: DateTimeFieldProps): React.ReactElement {
  // Плоский объект стилей: RN-стили — это не CSS, и в DOM их надо разворачивать самому.
  const flat = useMemo(() => (StyleSheet.flatten(style) ?? {}) as Record<string, unknown>, [style]);

  const background = asString(flat.backgroundColor) ?? '#0f172a';
  const foreground = asString(flat.color) ?? '#f8fafc';
  const shown = value.trim();

  /** Рамка поля: её рисует обёртка, потому что сам `<input>` теперь невидим. */
  const wrapCss: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: background,
    color: foreground,
    borderRadius: asNumber(flat.borderRadius) ?? 12,
    borderWidth: asNumber(flat.borderWidth) ?? 1,
    borderStyle: 'solid',
    borderColor: asString(flat.borderColor) ?? '#334155',
    padding: asNumber(flat.padding) ?? 12,
    fontSize: 16,
    width: '100%',
    boxSizing: 'border-box',
    // Высота как у соседних полей формы: у нативного поля даты она своя и чуть другая.
    minHeight: 46,
  };

  const textCss: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    // Пустое поле показывает подсказку — приглушённо, как placeholder у обычного поля.
    opacity: shown ? 1 : 0.55,
  };

  const inputCss: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    margin: 0,
    padding: 0,
    border: 0,
    background: 'transparent',
    // Невидимо, но нажимаемо: opacity не влияет на попадание, и системный календарь
    // открывается ровно как раньше. `visibility: hidden` или `display: none` — не влияют.
    opacity: 0,
    // Без явного размера мобильная Safari масштабирует страницу при фокусе на поле мельче 16 px.
    fontSize: 16,
    cursor: 'pointer',
    // Календарь рисует браузер по этому свойству; схема — от фактической яркости поля,
    // а не прибита к 'dark': иначе в светлых темах он выходит тёмным.
    colorScheme: isDarkSurface(background) ? 'dark' : 'light',
  };

  return (
    <div style={wrapCss}>
      <span style={textCss}>{shown || placeholder}</span>
      <CalendarGlyph kind={kind} />
      <input
        type={kind}
        value={kind === 'date' ? displayDateToIso(value) : value}
        aria-label={accessibilityLabel}
        style={inputCss}
        /*
          Границы календаря — чтобы недопустимую дату нельзя было ВЫБРАТЬ, а не только чтобы
          её потом отвергли: промотать барабан за границу тогда просто нечем. Приходят снаружи,
          от той формы, которой они принадлежат, — у даты рождения это возрастная вилка, у
          напоминания границ нет вовсе. Проверка при сохранении остаётся: она одна на обе
          платформы, а на нативной этих атрибутов нет.
        */
        min={kind === 'date' ? minDate : undefined}
        max={kind === 'date' ? maxDate : undefined}
        onChange={(e) =>
          onChange(kind === 'date' ? isoDateToDisplay(e.target.value) : e.target.value)
        }
      />
    </div>
  );
}

/**
 * Значок поля. Своей отрисовкой, а не системной иконкой поля: `<input>` теперь прозрачен, и
 * вместе с ним пропала бы и она. `currentColor` — чтобы значок жил в цвете темы, как и текст.
 */
function CalendarGlyph({ kind }: { kind: DateTimeFieldProps['kind'] }): React.ReactElement {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0, opacity: 0.75 }}>
      {kind === 'date' ? (
        <>
          <rect x={3} y={5} width={18} height={16} rx={3} />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </>
      ) : (
        <>
          <circle cx={12} cy={12} r={9} />
          <path d="M12 7v5l3 2" />
        </>
      )}
    </svg>
  );
}

function asString(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

function asNumber(v: unknown): number | undefined {
  return typeof v === 'number' ? v : undefined;
}

/**
 * Тёмная ли поверхность поля — по яркости его фона.
 *
 * Разбираем только те формы записи, что реально приходят из палитр (`#rgb`, `#rrggbb`,
 * `rgb()`/`rgba()`); всё непонятое считаем тёмным — это прежнее поведение, и на трёх тёмных
 * палитрах из пяти оно верное. Порог 0.5 по relative luminance: точность выше здесь не нужна,
 * решение бинарное и его цена — оттенок системного календаря.
 */
function isDarkSurface(color: string | undefined): boolean {
  if (!color) return true;
  const rgb = parseColor(color);
  if (!rgb) return true;
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.5;
}

function parseColor(input: string): [number, number, number] | null {
  const c = input.trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(c);
  if (hex) {
    const h = hex[1];
    const full =
      h.length === 3
        ? h
            .split('')
            .map((ch) => ch + ch)
            .join('')
        : h;
    return [
      parseInt(full.slice(0, 2), 16),
      parseInt(full.slice(2, 4), 16),
      parseInt(full.slice(4, 6), 16),
    ];
  }
  const fn = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(c);
  if (fn) return [Number(fn[1]), Number(fn[2]), Number(fn[3])];
  return null;
}
